import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminDataApi,
  type CaseDashboardResponse,
  type CaseRecordDto,
  type CaseOptionsDto
} from "../../services/admin-data-api";
import { useAuthStore } from "../../store/auth-store";

const priorityColor: Record<string, "default" | "info" | "warning" | "error" | "success"> = {
  LOW: "default",
  MEDIUM: "info",
  HIGH: "warning",
  URGENT: "error"
};

const statusColor: Record<string, "default" | "success" | "warning" | "info"> = {
  OPEN: "warning",
  IN_PROGRESS: "info",
  CLOSED: "success"
};

interface CaseFormState {
  title: string;
  participantId: string;
  description: string;
  assignedToId: string;
  priority: string;
  initialNote: string;
}

const defaultCaseForm: CaseFormState = {
  title: "",
  participantId: "",
  description: "",
  assignedToId: "",
  priority: "MEDIUM",
  initialNote: ""
};

export const CasesPage = () => {
  const navigate = useNavigate();
  const session = useAuthStore((state) => state.session);
  const canManage = session?.user.role === "ADMIN" || session?.user.role === "CASE_MANAGER";

  const [dashboard, setDashboard] = useState<CaseDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CaseFormState>(defaultCaseForm);
  const [options, setOptions] = useState<CaseOptionsDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadCases = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminDataApi.getCases();
      setDashboard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load cases");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCases().catch(() => {
      setError("Unable to load cases");
      setIsLoading(false);
    });
  }, []);

  const handleOpenDialog = async () => {
    try {
      const opts = await adminDataApi.getCaseOptions();
      setOptions(opts);
      setForm((prev) => ({
        ...prev,
        assignedToId: session?.user.id ?? ""
      }));
      setDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load case options");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm(defaultCaseForm);
  };

  const handleSubmit = async () => {
    if (!form.participantId || !form.title) {
      setError("Please provide a title and select a participant");
      return;
    }
    setIsSaving(true);
    try {
      await adminDataApi.createCase({
        title: form.title,
        description: form.description || undefined,
        participantId: form.participantId,
        assignedToId: form.assignedToId || undefined,
        priority: form.priority,
        initialNote: form.initialNote || undefined
      });
      await loadCases();
      handleCloseDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create case");
    } finally {
      setIsSaving(false);
    }
  };

  const caseCards = useMemo(() => dashboard?.cases ?? [], [dashboard]);

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="h4" component="h1">
            Case Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review all open cases, monitor workload, and launch new intakes.
          </Typography>
        </Grid>
        <Grid item>
          {canManage ? (
            <Button variant="contained" onClick={handleOpenDialog}>
              New Case
            </Button>
          ) : null}
        </Grid>
      </Grid>

      {isLoading ? <CircularProgress sx={{ display: "block", my: 4 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total cases
            </Typography>
            <Typography variant="h5">{dashboard?.stats.total ?? 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Cases by status
            </Typography>
            <Stack direction="row" spacing={2}>
              {Object.entries(dashboard?.stats.byStatus ?? {}).map(([status, count]) => (
                <Chip key={status} label={`${status.replace("_", " ")}: ${count}`} color="default" />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Stack spacing={2}>
        {caseCards.map((caseItem: CaseRecordDto) => (
          <Paper
            key={caseItem.id}
            variant="outlined"
            sx={{ p: 2, cursor: "pointer", "&:hover": { borderColor: "primary.light", boxShadow: 1 } }}
            onClick={() => navigate(`/cases/${caseItem.id}`)}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h6">{caseItem.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {caseItem.participant.firstName} {caseItem.participant.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <Chip label={caseItem.status.replace("_", " ")} color={statusColor[caseItem.status] ?? "default"} size="small" />
                  <Chip label={`Priority: ${caseItem.priority}`} color={priorityColor[caseItem.priority] ?? "default"} size="small" />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">
                  Assigned to: {caseItem.assignedTo?.email ?? "Unassigned"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated {dayjs(caseItem.updatedAt).format("MMM D, YYYY h:mm A")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Chip label={`Apps: ${caseItem._count?.applications ?? 0}`} size="small" />
                  <Chip label={`Notes: ${caseItem._count?.notes ?? 0}`} size="small" />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        ))}
        {!isLoading && caseCards.length === 0 ? (
          <Alert severity="info">No cases match your filters.</Alert>
        ) : null}
      </Stack>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>New Case Intake</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Case Title"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="case-participant-label">Participant</InputLabel>
                <Select
                  labelId="case-participant-label"
                  label="Participant"
                  value={form.participantId}
                  onChange={(event) => setForm((prev) => ({ ...prev, participantId: event.target.value }))}
                >
                  {(options?.participants ?? []).map((participant) => (
                    <MenuItem key={participant.id} value={participant.id}>
                      {participant.firstName} {participant.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="case-priority-label">Priority</InputLabel>
                <Select
                  labelId="case-priority-label"
                  label="Priority"
                  value={form.priority}
                  onChange={(event) => setForm((prev) => ({ ...prev, priority: event.target.value }))}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="case-assigned-label">Assign to staff</InputLabel>
                <Select
                  labelId="case-assigned-label"
                  label="Assign to staff"
                  value={form.assignedToId}
                  onChange={(event) => setForm((prev) => ({ ...prev, assignedToId: event.target.value }))}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {(options?.staff ?? []).map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.email} ({user.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Initial Note"
                value={form.initialNote}
                onChange={(event) => setForm((prev) => ({ ...prev, initialNote: event.target.value }))}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Create Case"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
